import { createContext, useCallback, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi, useAuth, useRealtimeEvent, useSessionClock, useSessionUserId, type AuthResponse } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { ReportIssueButton, SessionExpiryWarning, VersionBadge, microcopyToast, type IssueReport, type VersionInfo } from "@ncp/ui";

export type PlatformApp = "backoffice" | "portal" | "admin" | "assistant";

/** `/api/version`: anonymous callers get the version only; signed-in staff also the release notes, commit, build time and environment. */
export function useVersionInfo() {
  const api = useApi();
  const userId = useSessionUserId();
  return useQuery({ queryKey: ["version", userId ?? "anonymous"], queryFn: () => api.get<VersionInfo>("/api/version"), staleTime: 60 * 60 * 1000 });
}

/** The build's version with the "What's new" panel; opens by itself once after an update unless `autoOpen` is off. */
export function AppVersion({ autoOpen = true, className }: { autoOpen?: boolean; className?: string }) {
  const version = useVersionInfo();
  return <VersionBadge info={version.data} autoOpen={autoOpen} className={className} />;
}

/** "Report an issue" for the app's header: posts the note, the picture of the screen and its context to `POST /api/feedback`. */
export function ReportIssue({ app, className, labelled }: { app: PlatformApp; className?: string; labelled?: boolean }) {
  const api = useApi();
  const version = useVersionInfo();
  const submit = (report: IssueReport) => api.post("/api/feedback", { ...report, app, appVersion: version.data?.version }).then(() => undefined);
  return <ReportIssueButton onSubmit={submit} className={className} labelled={labelled} />;
}

const SessionUiContext = createContext(false);

/**
 * Marks that an app draws its own session messages (ECONIC's SessionManager: idle dialog, absolute notice, last sign-in). A
 * `SessionGuard` inside it renders nothing, so a frame that still mounts one never shows a second dialog.
 */
export function SessionUiProvider({ children }: { children: ReactNode }) {
  return <SessionUiContext.Provider value={true}>{children}</SessionUiContext.Provider>;
}

export function useSessionUiClaimed(): boolean {
  return useContext(SessionUiContext);
}

function timeOfDay(ms: number, locale: string): string {
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-AE-u-nu-latn" : "en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "Asia/Dubai" }).format(ms);
}

/**
 * The session warnings of the back office, admin and portal (security §3.3).
 * - Strict sessions: the idle warning two minutes before the 30-minute idle limit ("Stay signed in" renews the session and counts as
 *   activity), the absolute notice ten minutes before the 10-hour limit, and sign-out when the server revokes the session.
 * - Sessions from before the strict sign-in: the earlier two-minute "extend?" prompt before the token lapses.
 * The idle and absolute expiries themselves are enforced by the session provider, which takes the person to the sign-in page.
 */
export function SessionGuard({ onSignedOut }: { onSignedOut: () => void }) {
  const claimed = useSessionUiClaimed();
  if (claimed) return null;
  return <SessionGuardInner onSignedOut={onSignedOut} />;
}

function SessionGuardInner({ onSignedOut }: { onSignedOut: () => void }) {
  const api = useApi();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { session, setSession, clear, managed, refresh, signOut } = useAuth();
  const clock = useSessionClock();
  const leave = useCallback(() => { clear(); onSignedOut(); }, [clear, onSignedOut]);
  const expired = useCallback(() => { clear(); microcopyToast("sessionEnded"); onSignedOut(); }, [clear, onSignedOut]);

  useRealtimeEvent("session.revoked", () => signOut("revoked"), managed);

  if (managed) {
    return (
      <>
        <SessionExpiryWarning
          variant="idle"
          deadline={clock.idleDeadline}
          labels={{ title: t("auth.session.idleTitle"), body: t("auth.session.idleWarning", { time: "{time}" }), stay: t("auth.session.stay"), signOut: t("auth.session.signOutNow") }}
          onExtend={async () => { await refresh(); }}
          onSignOut={leave}
        />
        <SessionExpiryWarning
          variant="absolute"
          deadline={clock.absoluteDeadline}
          endsAtText={clock.absoluteDeadline ? timeOfDay(clock.absoluteDeadline, locale) : ""}
          labels={{ title: t("auth.session.absoluteTitle"), body: t("auth.session.absoluteWarning", { time: "{time}" }), stay: t("auth.session.stay"), signOut: t("auth.session.signOut") }}
          onSignOut={leave}
        />
      </>
    );
  }

  const extend = async () => {
    const next = await api.post<AuthResponse>("/api/auth/refresh", {});
    setSession(next);
    microcopyToast("sessionExtended", "success");
  };
  return <SessionExpiryWarning expiresAt={session?.expiresAt} onExtend={extend} onSignOut={leave} onExpired={expired} />;
}
